import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { green } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import moment from 'moment-timezone';
import React from 'react';
import { useHistory, useLocation } from 'react-router';

import FixedLinearProgress from '../components/FixedLinearProgress';
import { gridLoadingSlots } from '../components/GridLoadingOverlay';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import { useGridApiContext, useGridApiRef } from '@mui/x-data-grid-pro';
import { forwardRef, useImperativeHandle } from 'react';

import AgentMetricsListToolbar from './AgentMetricsListToolbar';
import AgentMetricsSummary from './AgentMetricsSummary';
import { canSeeAgentMetricsSummary } from './agentMetricsSummaryAccess';
import { byDayRangeAllowed, secondsToMinutes, weightedAverageResponseSeconds } from './agentMetricsByDay';

const STORAGE_KEY_AGENT_METRICS_TZ = 'agentMetrics_timezone';
/** Bumped to drop a legacy auto-saved `'UTC'` for everyone so the local tz becomes the default. */
const STORAGE_KEY_AGENT_METRICS_TZ_MIGRATION = 'agentMetrics_timezone_migration_v';
const AGENT_METRICS_TZ_MIGRATION_VERSION = '3';

(function migrateAgentMetricsTimezoneStorageOnce() {
    if (typeof window === 'undefined') return;
    try {
        if (window.localStorage.getItem(STORAGE_KEY_AGENT_METRICS_TZ_MIGRATION) === AGENT_METRICS_TZ_MIGRATION_VERSION) {
            return;
        }
        const legacy = window.localStorage.getItem(STORAGE_KEY_AGENT_METRICS_TZ);
        if (legacy === 'UTC' || legacy === 'Etc/UTC') {
            window.localStorage.removeItem(STORAGE_KEY_AGENT_METRICS_TZ);
        }
        window.localStorage.setItem(STORAGE_KEY_AGENT_METRICS_TZ_MIGRATION, AGENT_METRICS_TZ_MIGRATION_VERSION);
    } catch {
        /* quota / privacy mode */
    }
})();

/** Representative IANA zones for common offsets (fallback when Intl/guess return 'UTC' due to anti-fingerprinting). */
const OFFSET_MIN_TO_IANA_ZONE = {
    [-720]: 'Pacific/Midway',
    [-660]: 'Pacific/Pago_Pago',
    [-600]: 'Pacific/Honolulu',
    [-540]: 'America/Anchorage',
    [-480]: 'America/Los_Angeles',
    [-420]: 'America/Denver',
    [-360]: 'America/Chicago',
    [-300]: 'America/New_York',
    [-240]: 'America/New_York', // EDT
    [-180]: 'America/Sao_Paulo',
    [-120]: 'Atlantic/South_Georgia',
    [-60]: 'Atlantic/Azores',
    0: 'UTC',
    60: 'Europe/London',
    120: 'Europe/Berlin',
    180: 'Europe/Moscow',
    240: 'Asia/Dubai',
    300: 'Asia/Karachi',
    330: 'Asia/Kolkata',
    360: 'Asia/Dhaka',
    420: 'Asia/Bangkok',
    480: 'Asia/Shanghai',
    540: 'Asia/Tokyo',
    570: 'Australia/Adelaide',
    600: 'Australia/Sydney',
    660: 'Pacific/Noumea',
    720: 'Pacific/Auckland',
};

/**
 * Prefer Intl/moment guess; if those return 'UTC' due to anti-fingerprinting, fall back to system clock offset.
 * `zoneNowOffsetMin` is the resolved zone's offset right now in minutes east of UTC.
 */
function getLocalIanaTimezone() {
    let intlTz = null;
    try {
        intlTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        /* ignore */
    }
    let guessed = null;
    try {
        guessed = moment.tz.guess();
    } catch {
        /* ignore */
    }
    const candidate = intlTz && moment.tz.zone(intlTz) ? intlTz
        : (guessed && moment.tz.zone(guessed) ? guessed : null);
    let runtimeOffsetMin = 0;
    try {
        runtimeOffsetMin = -new Date().getTimezoneOffset();
    } catch {
        /* ignore */
    }
    if (candidate && candidate !== 'UTC' && candidate !== 'Etc/UTC') {
        return candidate;
    }
    // Intl/guess gave us UTC. If the system clock disagrees, use the offset to pick a representative zone.
    if (runtimeOffsetMin !== 0) {
        const fromOffset = OFFSET_MIN_TO_IANA_ZONE[runtimeOffsetMin];
        if (fromOffset && moment.tz.zone(fromOffset)) {
            return fromOffset;
        }
        try {
            const names = moment.tz.names();
            for (let i = 0; i < names.length; i++) {
                const name = names[i];
                if (moment.tz(name).utcOffset() === runtimeOffsetMin) {
                    return name;
                }
            }
        } catch {
            /* ignore */
        }
    }
    return candidate || 'UTC';
}

/**
 * Returns the operator's explicitly chosen tz, or the detected local tz.
 * The presence of a stored value is itself the "user chose" signal: we only persist on user action,
 * and we ignore anything that's not a valid IANA name.
 */
function readStoredMetricsTimezoneOrLocalDefault(storageKey) {
    try {
        const stored = window.localStorage.getItem(storageKey);
        if (stored && moment.tz.zone(stored)) {
            return stored;
        }
    } catch {
        /* quota / privacy mode */
    }
    return getLocalIanaTimezone();
}

const useStyles = makeStyles((theme) => ({

    root: {
        minWidth: '1400px',
        '& .MuiBox-root': {
            padding: '0 !important',
        },
    },
    recordListWrapper: {
        '& .MuiBox-root': {
            padding: '0 !important',
        },
        [theme.breakpoints.down('sm')]: {
            margin: '0 0.5rem !important',
        },
    },
    recordList: {
        border: 'none !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        height: `calc(100vh - 17.5rem) !important`,
        maxHeight: `calc(100vh - 17.5rem) !important`,
        [theme.breakpoints.down('sm')]: {
            height: `calc(100vh - 25.2rem) !important`,
            maxHeight: `calc(100vh - 25.2rem) !important`,
        },
        '& .MuiDataGrid-iconButtonContainer': {
            display: 'none !important',
        },
        '& .MuiDataGrid-sortIcon': {
            display: 'none !important',
        },
    },
    listItem: {},
    dm_enabled: {
        color: green[800],
        fontWeight: 'bold',
    },
    dm_disabled: {
        textDecoration: 'line-through',
    },
    mainContainer: {
        margin: '1rem !important', border: 'solid 1px #D8D8D8 !important', borderRadius: '12px !important'
        ,
        [theme.breakpoints.down('sm')]: {
            margin: '0rem !important', border: 'none !important', borderRadius: '12px !important'
        },

    }
}));

/**
 * CP-8263: the agent's email ships in the CSV export only — the grid links to each user
 * instead. Hidden by default rather than pinned off, so the column menu can still show it.
 */
const DEFAULT_COLUMN_VISIBILITY = { user_email: false };

/**
 * Columns only a backend with the response-time / by-day report fills in. Against an older
 * backend they would sit empty, so they stay hidden until a response carries the fields
 * (the dashboard can then deploy ahead of the backend).
 */
const NEW_METRIC_FIELDS = new Set([
    'report_day',
    'agent_type',
    'conversations_answered_count',
    'avg_response_seconds',
    'median_response_seconds',
    'conversations_closed_count',
]);
const STORAGE_KEY_AGENT_METRICS_COLUMN_VIS = 'AgentMetricsList.columnVisibilityModel';

/** Stored choices win over the defaults, so a column the user un-hid stays un-hidden. */
function readStoredColumnVisibility() {
    try {
        const saved = window.localStorage.getItem(STORAGE_KEY_AGENT_METRICS_COLUMN_VIS);
        if (saved) {
            return { ...DEFAULT_COLUMN_VISIBILITY, ...JSON.parse(saved) };
        }
    } catch {
        /* quota / privacy mode / malformed */
    }
    return DEFAULT_COLUMN_VISIBILITY;
}

const NO_SORT = [];

const FILTER_FIELDS = ['agent_ids', 'start_datetime', 'end_datetime', 'tour_ids', 'tag_ids', 'access_group_ids', 'exclude_guides', 'group_by_day'];

/**
 * The filters and query a page load starts with: the URL's, every field present, and the
 * last 7 days when the URL names no dates. Read before the first render (2026-10-29): read
 * in an effect, it came after the grid's own first fetch, which then asked for an all-time
 * report -- the most expensive one -- with no date range, and the server ran it to the end
 * after the browser had dropped it.
 */
export function readAgentMetricsUrlState(search, timezone) {
    const params = new URLSearchParams(search);
    const filters = [];
    let query = '';
    let hasUrlDateFilter = false;
    for (const [key, value] of params.entries()) {
        if (key === 'query') {
            query = value;
            continue;
        } else if (key === 'tab') {
            continue;
        }
        try {
            const f = JSON.parse(value);
            if (f && (f.field === 'start_datetime' || f.field === 'end_datetime') && f.value) {
                hasUrlDateFilter = true;
            }
            filters.push(f);
        } catch {
            /* skip malformed */
        }
    }

    FILTER_FIELDS.forEach((field) => {
        if (!filters.some((filter) => filter.field === field)) {
            filters.push({ field, value: null });
        }
    });

    if (!hasUrlDateFilter) {
        const today = moment.tz(timezone).format('YYYY-MM-DD');
        const sevenDaysAgo = moment.tz(timezone).subtract(7, 'days').format('YYYY-MM-DD');
        const startIdx = filters.findIndex((f) => f.field === 'start_datetime');
        const endIdx = filters.findIndex((f) => f.field === 'end_datetime');
        if (startIdx >= 0) filters[startIdx] = { ...filters[startIdx], value: sevenDaysAgo };
        if (endIdx >= 0) filters[endIdx] = { ...filters[endIdx], value: today };
    }
    return { filters, query };
}

const getCurrentTab = (urlSearchParams, currentPath) => {
    if ((new URLSearchParams(urlSearchParams).get('tab'))) {
        return (new URLSearchParams(urlSearchParams).get('tab'))
    }
    return 'agent_metrics'
}

const ExportCSVHiddenToolbar = forwardRef((props, ref) => {
    const apiRef = useGridApiContext();

    // Expose functions or values via the forwarded ref
    useImperativeHandle(ref, () => ({
        exportDataAsCsv: (options) => {
            if (apiRef.current?.exportDataAsCsv) {
                apiRef.current.exportDataAsCsv(options);
            } else {
                console.warn('CSV export not available — are you using @mui/x-data-grid-pro?');
            }
        },
        // Add more methods as needed
    }));

    return (
        null
    );
});

// Over the rows while the report loads: the first one can take ten seconds on a large operator.
const REPORT_SLOTS = gridLoadingSlots('Loading the report… this can take a few seconds');

const AgentMetricsList = (props) => {
    const history = useHistory();
    const resource = 'report/staff';
    const classes = useStyles();
    const [selectedIds] = React.useState([]);
    const [lastUpdate, setLastUpdate] = React.useState();
    const [syncActive, setSyncActive] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');
    const { roleMap, tourOperator } = React.useContext(ContextStore);
    // The summary above the grid: every operator's, unless its config.features.agent_metrics_summary
    // is false (agentMetricsSummaryAccess.js).
    const showSummary = canSeeAgentMetricsSummary(tourOperator);
    const [relatedLoading] = React.useState(false);
    const location = useLocation();
    const [currentTab] = React.useState(getCurrentTab(location.search, location.pathname));
    const dataGridApiRef = useGridApiRef();
    const toolbarRef = React.useRef();
    const [rows, setRows] = React.useState([]);
    const [columnVisibility, setColumnVisibility] = React.useState(readStoredColumnVisibility);
    // The totals row's median comes from the server: medians do not combine, so it is taken
    // over every response time of the rows shown. Keyed by the response's first row (which
    // RecordList passes on as-is), so a slower, superseded response can't put its median under
    // the newer rows.
    const totalsByFirstRow = React.useRef(new WeakMap());
    // Until the first report answers, the summary shows dashes rather than zeros.
    const [reportLoaded, setReportLoaded] = React.useState(false);

    // Latched: once a response has carried the new fields, an empty range keeps the columns.
    const [newMetricsAvailable, setNewMetricsAvailable] = React.useState(false);
    React.useEffect(() => {
        if (rows.some((r) => r && Object.prototype.hasOwnProperty.call(r, 'avg_response_seconds'))) {
            setNewMetricsAvailable(true);
        }
    }, [rows]);

    const handleColumnVisibilityChange = React.useCallback((model) => {
        setColumnVisibility(model);
        try {
            window.localStorage.setItem(STORAGE_KEY_AGENT_METRICS_COLUMN_VIS, JSON.stringify(model));
        } catch {
            /* quota / privacy mode */
        }
    }, []);

    const [metricsTimezone, setMetricsTimezone] = React.useState(() =>
        readStoredMetricsTimezoneOrLocalDefault(STORAGE_KEY_AGENT_METRICS_TZ)
    );
    const [initialUrlState] = React.useState(() => readAgentMetricsUrlState(location.search, metricsTimezone));
    const [recordListFilters, setRecordListFilters] = React.useState(initialUrlState.filters);
    // '' rather than null when the URL has none: the toolbar reports '' on mount, and a
    // change from null would make the grid fetch the report again.
    const [query, setQuery] = React.useState(initialUrlState.query);
    const handleMetricsTimezoneChange = React.useCallback((tz) => {
        if (!tz || !moment.tz.zone(tz)) return;
        try {
            window.localStorage.setItem(STORAGE_KEY_AGENT_METRICS_TZ, tz);
        } catch {
            /* ignore */
        }
        setMetricsTimezone(tz);
    }, []);

    // Update the URL whenever the filters or query change
    React.useEffect(() => {
        const params = new URLSearchParams();
        let filters = [...recordListFilters]
        filters.forEach((filter, index) => {
            if (filter.value) {
                params.append(index, JSON.stringify(filter));
            }
        });

        // Add the query to the URL parameters
        if (query) {
            params.append('query', query);
        }

        params.set('tab', currentTab);
        history.push({
            pathname: location.pathname,
            search: params.toString(),
        });
    }, [recordListFilters, query]);


    // CP-8263: `allColumns` picks up the Email column, which the grid keeps hidden
    // (rows link to the user page there, so the address is only useful in the file).
    const onExportList = async () => {
        toolbarRef.current?.exportDataAsCsv({ allColumns: true });
    }

    const totalRow = React.useMemo(() => {
        const totals = {
            id: 'totals',
            user_display_name: 'Totals',
            conversations_opened_count: 0,
            messages_opened_count: 0,
            messages_closed_count: 0,
            messages_sent_count: 0,
            conversations_marked_issues_closed: 0,
            conversations_marked_issues_open: 0,
            initiated_msg_to_guest_count: 0,
            initiated_msg_to_guide_count: 0,
            guest_messages_opened_count: 0,
            guide_messages_opened_count: 0,
            reply_to_guest_count: 0,
            reply_to_guide_count: 0,
            action_not_taken_after_opening_count: 0,
            internal_tag_added_to_conversation_count: 0,
            conversations_answered_count: 0,
            conversations_closed_count: 0,
            avg_response_seconds: weightedAverageResponseSeconds(rows),
            median_response_seconds: (rows[0] && totalsByFirstRow.current.get(rows[0])?.median_response_seconds) ?? null,
        };

        rows.forEach((row) => {
            totals.conversations_opened_count += row.conversations_opened_count || 0;
            totals.messages_opened_count += row.messages_opened_count || 0;
            totals.messages_closed_count += row.messages_closed_count || 0;
            totals.messages_sent_count += row.messages_sent_count || 0;
            totals.conversations_marked_issues_closed += row.conversations_marked_issues_closed || 0;
            totals.conversations_marked_issues_open += row.conversations_marked_issues_open || 0;
            totals.initiated_msg_to_guest_count += row.initiated_msg_to_guest_count || 0;
            totals.initiated_msg_to_guide_count += row.initiated_msg_to_guide_count || 0;
            totals.guest_messages_opened_count += row.guest_messages_opened_count || 0;
            totals.guide_messages_opened_count += row.guide_messages_opened_count || 0;
            totals.reply_to_guest_count += row.reply_to_guest_count || 0;
            totals.reply_to_guide_count += row.reply_to_guide_count || 0;
            totals.action_not_taken_after_opening_count += row.action_not_taken_after_opening_count || 0;
            totals.internal_tag_added_to_conversation_count += row.internal_tag_added_to_conversation_count || 0;
            totals.conversations_answered_count += row.conversations_answered_count || 0;
            totals.conversations_closed_count += row.conversations_closed_count || 0;
        });

        return [totals];
    }, [rows]);

    // Helper function to create header with help icon
    const createHeaderWithHelp = (headerName, explanation, isFirstColumn = false) => {
        return (
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', paddingTop: '20px' }}>
                <Tooltip 
                    title={explanation} 
                    arrow 
                    placement={isFirstColumn ? "top-start" : "top"}
                    PopperProps={{
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: isFirstColumn ? [-40, 8] : [0, 8],
                                },
                            },
                            {
                                name: 'preventOverflow',
                                options: {
                                    boundary: 'viewport',
                                    padding: 8,
                                },
                            },
                        ],
                    }}
                >
                    <IconButton 
                        size="small" 
                        style={{ 
                            position: 'absolute',
                            top: '0',
                            padding: '2px', 
                            color: '#666',
                            zIndex: 1
                        }} 
                        onClick={(e) => e.stopPropagation()}
                    >
                        <HelpOutlineIcon fontSize="small" style={{ fontSize: '14px' }} />
                    </IconButton>
                </Tooltip>
                <Typography 
                    variant="body2" 
                    style={{ 
                        fontSize: '0.875rem',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        textAlign: 'center',
                        lineHeight: '1.2',
                        paddingBottom: '2px'
                    }}
                >
                    {headerName}
                </Typography>
            </div>
        );
    };

    // By day only runs for a range the backend accepts (a start date, at most 93 days).
    // The choice stays in the filters, so going back to a short range brings it back.
    const filterValue = (field) => recordListFilters.find((f) => f.field === field)?.value;
    const byDayAllowed = byDayRangeAllowed(filterValue('start_datetime'), filterValue('end_datetime'), metricsTimezone);
    const byDay = byDayAllowed && (filterValue('group_by_day') === true || filterValue('group_by_day') === 'true');

    const apiFiltersWithTimezone = React.useMemo(
        () => [
            ...recordListFilters.filter((f) => f.field !== 'group_by_day'),
            { field: 'group_by_day', value: byDay },
            { field: 'timezone', value: metricsTimezone },
        ],
        [recordListFilters, metricsTimezone, byDay]
    );
    // Whether the grid's report for what is on screen has answered. Worked out while rendering, from
    // the request the answer belongs to: set in an effect, the flag came a render late and the
    // summary (whose effect runs first) started its reports beside the table's on every filter
    // change (2026-10-29). RecordList hands each request the `convert` of the render that made it,
    // so the key captured below is that request's.
    const tableRequestKey = JSON.stringify([apiFiltersWithTimezone, query, lastUpdate ?? null]);
    const [answeredRequestKey, setAnsweredRequestKey] = React.useState(null);
    const tableLoading = answeredRequestKey !== tableRequestKey;
    const captureTotals = React.useCallback((json) => {
        const first = json?.results?.[0];
        if (first && typeof first === 'object') {
            totalsByFirstRow.current.set(first, json?.meta?.totals || null);
        }
        if (json && !json?.meta?.busy) {
            setReportLoaded(true);
        }
        setAnsweredRequestKey(tableRequestKey);
        return json;
    }, [tableRequestKey]);

    const responseMinutesColumn = (field, headerName, smallHeaderName, explanation) => ({
        field,
        headerName: isSmallScreen ? smallHeaderName : headerName,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        type: 'number',
        flex: 1,
        minWidth: isSmallScreen ? 100 : 110,
        valueGetter: (p) => secondsToMinutes(p.row[field]),
        renderHeader: () => createHeaderWithHelp(isSmallScreen ? smallHeaderName : headerName, explanation),
    });

    const all_record_list_cols = [
        ...(byDay ? [{
            field: 'report_day',
            headerName: 'Date',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: 110,
            renderHeader: () => createHeaderWithHelp('Date', 'The day this row covers, in the timezone the dates are shown in', true),
        }] : []),
        {
            field: 'user_display_name',
            headerName: 'Agent',
            headerAlign: 'center',
            sortable: false,
            flex: !isSmallScreen ? 3 : 2,
            minWidth: isSmallScreen ? 150 : 200,
            renderHeader: () => createHeaderWithHelp('Agent', 'The name of the agent whose metrics are displayed', true),
            renderCell: p => {
                const ref = `/user/${p.row.user_id}?tab=1`
                
                return (
                    p.row.user_id ? <a href={ref} title={p.row.user_display_name} style={{ color: "#007AFF", overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{p.row.user_display_name}</a> : p.row.user_display_name
                ) 
            }
        },
        {
            // Hidden in the grid (see DEFAULT_COLUMN_VISIBILITY), always exported to CSV.
            field: 'user_email',
            headerName: 'Email',
            headerAlign: 'center',
            sortable: false,
            flex: 2,
            minWidth: 200,
        },
        {
            field: 'agent_type',
            headerName: isSmallScreen ? 'Type' : 'Agent Type',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1.5,
            minWidth: isSmallScreen ? 100 : 130,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Type' : 'Agent Type',
                'The agent\'s staff role, for example Admin or Tour Guide. An agent can have more than one.'
            )
        },
        {
            field: 'conversations_opened_count',
            headerName: isSmallScreen ? 'Conv. Opened' : 'Conversations Opened',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 110 : 130,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Conv. Opened' : 'Conversations Opened',
                'Number of conversations the agent opened during the selected time period'
            )
        },
        {
            field: 'guest_messages_opened_count',
            headerName: isSmallScreen ? 'Guest Msgs Read' : 'Guest Messages Read',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 100 : 100,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Guest Msgs Read' : 'Guest Messages Read',
                'Number of messages from guests that the agent read'
            )
        },
        {
            field: 'reply_to_guest_count',
            headerName: isSmallScreen ? 'Replies To Guests' : 'Replies To Guests',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 110 : 90,
            renderHeader: () => createHeaderWithHelp(
                'Replies To Guests',
                'Number of messages the agent sent in response to guest messages'
            )
        },
        {
            field: 'conversations_answered_count',
            headerName: isSmallScreen ? 'Conv. Answered' : 'Conversations Answered',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 110 : 120,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Conv. Answered' : 'Conversations Answered',
                'Number of different conversations in which the agent replied to a guest'
            )
        },
        responseMinutesColumn(
            'avg_response_seconds',
            'Avg. Response Time (min)',
            'Avg. Resp. (min)',
            'Average time in minutes from a guest\'s first unanswered message to the agent\'s reply. Counts the same replies as Replies To Guests. A guest message sent more than 48 hours after the previous message starts a new question.'
        ),
        responseMinutesColumn(
            'median_response_seconds',
            'Median Response Time (min)',
            'Median Resp. (min)',
            'The middle response time in minutes: half of the agent\'s replies were faster than this. Questions that waited overnight affect it far less than the average.'
        ),
        {
            field: 'initiated_msg_to_guest_count',
            headerName: isSmallScreen ? 'Init. To Guests' : 'Messages Initiated To Guests',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 100 : 100,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Init. To Guests' : 'Messages Initiated To Guests',
                'Number of messages the agent sent to guests without the guest having sent a message first'
            )
        },
        {
            field: 'guide_messages_opened_count',
            headerName: isSmallScreen ? 'Guide Msgs Read' : 'Guide Messages Read',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 100 : 100,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Guide Msgs Read' : 'Guide Messages Read',
                'Number of messages from guides that the agent read'
            )
        },
        {
            field: 'reply_to_guide_count',
            headerName: isSmallScreen ? 'Replies To Guides' : 'Replies To Guides',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 110 : 90,
            renderHeader: () => createHeaderWithHelp(
                'Replies To Guides',
                'Number of messages the agent sent in response to guide messages'
            )
        },
        {
            field: 'initiated_msg_to_guide_count',
            headerName: isSmallScreen ? 'Init. To Guides' : 'Messages Initiated To Guides',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 100 : 100,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Init. To Guides' : 'Messages Initiated To Guides',
                'Number of messages the agent sent to guides without the guide having sent a message first'
            )
        },
        {
            field: 'messages_sent_count',
            headerName: isSmallScreen ? 'Total Sent' : 'Total Messages Sent',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 80 : 100,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Total Sent' : 'Total Messages Sent',
                'Total number of messages the agent sent (includes replies and initiated messages to both guests and guides)'
            )
        },
        {
            field: 'conversations_closed_count',
            headerName: isSmallScreen ? 'Conv. Closed' : 'Conversations Closed',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 100 : 110,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Conv. Closed' : 'Conversations Closed',
                'Number of different conversations whose issues the agent closed, by replying (which closes them automatically) or by marking them closed'
            )
        },
        {
            field: 'conversations_marked_issues_closed',
            headerName: isSmallScreen ? 'Issues Closed' : 'Chat Issues Marked Closed',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 100 : 100,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Issues Closed' : 'Chat Issues Marked Closed',
                'Number of times the agent closed a conversation\'s issues. Every message an agent sends closes them automatically, so this follows Total Messages Sent plus manual closes.'
                + (newMetricsAvailable ? ' For a count of conversations, see Conversations Closed.' : '')
            )
        },
        {
            field: 'conversations_marked_issues_open',
            headerName: isSmallScreen ? 'Issues Open' : 'Chat Issues Marked Open',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 95 : 95,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Issues Open' : 'Chat Issues Marked Open',
                'Number of conversations where the agent marked the conversation\'s issues as open (note that issues are also automatically opened when a new guest message is received)'
            )
        },
        {
            field: 'messages_closed_count',
            headerName: isSmallScreen ? 'Marked Unread' : 'Conversations Marked Unread',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 100 : 120,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Marked Unread' : 'Conversations Marked Unread',
                'Number of conversations the agent marked as unread'
            )
        },
        {
            field: 'messages_opened_count',
            headerName: isSmallScreen ? 'Total Read' : 'Total Messages Read',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 85 : 100,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Total Read' : 'Total Messages Read',
                'Total number of messages the agent read (includes messages from both guests and guides)'
            )
        },
        {
            field: 'action_not_taken_after_opening_count',
            headerName: isSmallScreen ? 'No Action' : 'No Action Taken',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 80 : 90,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'No Action' : 'No Action Taken',
                'Number of conversations the agent opened but did not send a message or take any other action'
            )
        },
        {
            field: 'internal_tag_added_to_conversation_count',
            headerName: isSmallScreen ? 'Tags Added' : 'Internal Tags Added',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 90 : 100,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Tags Added' : 'Internal Tags Added',
                'Number of internal tags the agent added to conversations'
            )
        },
        {  
            field: 'shared_messages_opened_count',
            headerName: isSmallScreen ? 'Shared Agent Read' : 'Messages Read by Shared Agent',
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            flex: 1,
            minWidth: isSmallScreen ? 120 : 120,
            renderHeader: () => createHeaderWithHelp(
                isSmallScreen ? 'Shared Agent Read' : 'Messages Read by Shared Agent',
                'Number of messages read by a shared agent (agent who has access to the conversation but is not the primary agent)'
            )
        },
    ];
    const record_list_cols = all_record_list_cols.filter((c) => newMetricsAvailable || !NEW_METRIC_FIELDS.has(c.field));

    return (
        <div className={classes.root}>
            <PageMeta title="Analytics" />
            <FixedLinearProgress visibility={syncActive || tableLoading} />

            <Grid item xs={12} p={isSmallScreen && 2} rb={0} style={{marginBottom: !isSmallScreen ? 48 : 46}}>
                <AgentMetricsListToolbar
                    resource={resource}
                    query={query}
                    setRecordListFilters={setRecordListFilters}
                    setQuery={setQuery}
                    selectedIds={selectedIds}
                    isSmallScreen={isSmallScreen}
                    roleMap={roleMap}
                    recordListFilters={recordListFilters}
                    metricsTimezone={metricsTimezone}
                    onMetricsTimezoneChange={handleMetricsTimezoneChange}
                    setLastUpdate={setLastUpdate}
                    setSyncActive={setSyncActive}
                    onExportList={onExportList}
                    byDayAllowed={byDayAllowed}
                    byDay={byDay}
                    byDaySupported={newMetricsAvailable}
                />
            </Grid>
            {showSummary && (
            <Grid item xs={12}>
                <AgentMetricsSummary
                    rows={rows}
                    reportLoaded={reportLoaded}
                    medianSeconds={totalRow[0].median_response_seconds}
                    avgSeconds={totalRow[0].avg_response_seconds}
                    apiFilters={apiFiltersWithTimezone}
                    query={query}
                    startDate={filterValue('start_datetime')}
                    endDate={filterValue('end_datetime')}
                    timezone={metricsTimezone}
                    byDay={byDay}
                    refreshKey={lastUpdate}
                    isSmallScreen={isSmallScreen}
                    waitForTable={tableLoading}
                />
            </Grid>
            )}
            <Grid item xs={12} className={classes.recordListWrapper}>
                <RecordList
                    className={classes.recordList}
                    resource={resource}
                    disableSelectionOnClick={true}
                    apiFilters={apiFiltersWithTimezone}
                    query={query}
                    // No starting sort. There is no `name` column here: the grid dropped
                    // a `name` sort on mount, reported the empty sort as a change, and the
                    // list fetched the report a second time (2026-10-29).
                    sortModel={NO_SORT}
                    deps={lastUpdate}
                    // No auto-refresh here (same as Notification Metrics): every load is a
                    // ClickHouse report, and a grid left on "every 5 seconds" fired 54 of
                    // them in 4.5 minutes and took the events service past its memory
                    // ceiling (CPBACKEND-BF36, 2026-09-17). The metrics change slowly;
                    // the toolbar refresh is the way to re-run them.
                    autorefresh={false}
                    disableColumnFilter={true}
                    isSmallScreen={isSmallScreen}
                    //listItemRenderer={ListItemRenderer}
                    checkboxSelection={false}
                    disablePageUrlData={true}
                    perPage={100}
                    relatedLoading={relatedLoading}
                    columns={record_list_cols}
                    columnVisibilityModel={columnVisibility}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    //flavor="pro"
                    paginationMode="client"
                    sortingMode="client"
                    toolbar={ ExportCSVHiddenToolbar }
                    slotProps={{ toolbar: { ref: toolbarRef, printOptions: { disableToolbarButton: true } } }}
                    apiRef={dataGridApiRef}
                    colTitlesMultiLine={true}
                    colTitlesMultiLineHeight={85}
                    flavor="pro"
                    // No totals row before the first report: a row of zeros read as real figures.
                    totalRow={reportLoaded ? totalRow : undefined}
                    slots={REPORT_SLOTS}
                    setRows={setRows}
                    convert={captureTotals}
                    // Agent names stay in view while the metrics scroll sideways.
                    initialState={{ pinnedColumns: { left: ['user_display_name'] } }}
                />
            </Grid>
        </div>
    );
};

export default AgentMetricsList;
